/* eslint-disable no-bitwise */
// Tính mã OTP (TOTP - RFC 6238) từ secret 2FA, thuần JS không cần thư viện ngoài.

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const sha1 = (bytes) => {
  const messageLengthBits = bytes.length * 8;
  const padded = new Uint8Array((((bytes.length + 8) >> 6) + 1) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;

  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(messageLengthBits / 4294967296), false);
  view.setUint32(padded.length - 4, messageLengthBits >>> 0, false);

  let h0 = 0x67452301;
  let h1 = 0xEFCDAB89;
  let h2 = 0x98BADCFE;
  let h3 = 0x10325476;
  let h4 = 0xC3D2E1F0;

  const w = new Uint32Array(80);

  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i++) {
      w[i] = view.getUint32(offset + i * 4, false);
    }
    for (let i = 16; i < 80; i++) {
      const n = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
      w[i] = (n << 1) | (n >>> 31);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let i = 0; i < 80; i++) {
      let f;
      let k;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5A827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ED9EBA1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8F1BBCDC;
      } else {
        f = b ^ c ^ d;
        k = 0xCA62C1D6;
      }

      const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = ((b << 30) | (b >>> 2)) >>> 0;
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const digest = new Uint8Array(20);
  const digestView = new DataView(digest.buffer);
  [h0, h1, h2, h3, h4].forEach((h, i) => digestView.setUint32(i * 4, h, false));
  return digest;
};

const concatBytes = (a, b) => {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
};

const hmacSha1 = (key, message) => {
  const blockSize = 64;
  const normalizedKey = new Uint8Array(blockSize);
  normalizedKey.set(key.length > blockSize ? sha1(key) : key);

  const innerPad = new Uint8Array(blockSize);
  const outerPad = new Uint8Array(blockSize);
  for (let i = 0; i < blockSize; i++) {
    innerPad[i] = normalizedKey[i] ^ 0x36;
    outerPad[i] = normalizedKey[i] ^ 0x5c;
  }

  return sha1(concatBytes(outerPad, sha1(concatBytes(innerPad, message))));
};

export const base32Decode = (input) => {
  const clean = String(input || '')
    .toUpperCase()
    .replace(/[\s\-_]/g, '')
    .replace(/=+$/, '');

  if (!clean) {
    throw new Error('Secret 2FA rỗng');
  }

  const bytes = [];
  let bits = 0;
  let value = 0;

  for (let i = 0; i < clean.length; i++) {
    const index = BASE32_ALPHABET.indexOf(clean[i]);
    if (index === -1) {
      throw new Error(`Secret 2FA không hợp lệ (ký tự "${clean[i]}")`);
    }
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }

  if (!bytes.length) {
    throw new Error('Secret 2FA không hợp lệ');
  }

  return new Uint8Array(bytes);
};

// Nhận secret dạng thô ("abcd efgh ...") hoặc URI otpauth://totp/...?secret=...
export const parseTotpSecret = (raw) => {
  const input = String(raw || '').trim();
  if (!input) {
    throw new Error('Chưa có mã 2FA');
  }

  const result = {
    secret: input,
    digits: 6,
    period: 30,
    algorithm: 'SHA1',
    label: ''
  };

  if (/^otpauth:\/\//i.test(input)) {
    const queryIndex = input.indexOf('?');
    const params = new URLSearchParams(queryIndex >= 0 ? input.slice(queryIndex + 1) : '');
    const secret = params.get('secret');
    if (!secret) {
      throw new Error('URI otpauth không chứa secret');
    }
    result.secret = secret;
    result.digits = parseInt(params.get('digits'), 10) || 6;
    result.period = parseInt(params.get('period'), 10) || 30;
    result.algorithm = (params.get('algorithm') || 'SHA1').toUpperCase();
    try {
      result.label = decodeURIComponent(input.slice(input.indexOf('//') + 2, queryIndex >= 0 ? queryIndex : undefined));
    } catch (e) {
      result.label = '';
    }
  }

  if (result.algorithm !== 'SHA1') {
    throw new Error(`Chỉ hỗ trợ thuật toán SHA1 (nhận được ${result.algorithm})`);
  }

  return result;
};

// Trả về { code, period, digits, remaining, secret } tại thời điểm timestamp (ms)
export const generateTOTP = (raw, timestamp = Date.now()) => {
  const { secret, digits, period, label } = parseTotpSecret(raw);
  const key = base32Decode(secret);

  const epochSeconds = Math.floor(timestamp / 1000);
  const counter = Math.floor(epochSeconds / period);

  const counterBytes = new Uint8Array(8);
  const counterView = new DataView(counterBytes.buffer);
  counterView.setUint32(0, Math.floor(counter / 4294967296), false);
  counterView.setUint32(4, counter >>> 0, false);

  const hash = hmacSha1(key, counterBytes);
  const offset = hash[hash.length - 1] & 0x0f;
  const binary =
    ((hash[offset] & 0x7f) << 24) |
    ((hash[offset + 1] & 0xff) << 16) |
    ((hash[offset + 2] & 0xff) << 8) |
    (hash[offset + 3] & 0xff);

  const code = String(binary % 10 ** digits).padStart(digits, '0');

  return {
    code,
    digits,
    period,
    label,
    secret,
    remaining: period - (epochSeconds % period)
  };
};
